import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Eyebrow } from "@/components/ladder/Offers";
import { Check } from "lucide-react";

/** Real Smash photos + verified milestones, both filled from Smash HQ. Empty slots don't render. */
export const Credibility = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/credibility").then(({ data }) => setData(data));
  }, []);

  if (!data) return null;
  const photos = (data.photos || []).filter((p) => p.url);
  const milestones = (data.milestones || []).filter((m) => m.label);
  const webm = data.clip?.endsWith(".mp4") ? data.clip.replace(/\.mp4$/, ".webm") : null;

  return (
    <section data-testid="credibility-section" className="mx-auto max-w-6xl px-5 py-20 md:px-10 md:py-28">
      <Eyebrow>{data.eyebrow}</Eyebrow>
      <h2 className="neon mt-3 max-w-4xl text-4xl leading-tight sm:text-5xl">{data.title}</h2>
      {data.intro ? (
        <p className="mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">{data.intro}</p>
      ) : null}

      {data.clip ? (
        <figure data-testid="credibility-clip" className="mt-12 overflow-hidden border border-border bg-black">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="h-full w-full object-cover"
            style={{ maxHeight: 520 }}
            {...(webm ? {} : { src: data.clip })}
          >
            {webm ? <source src={webm} type="video/webm" /> : null}
            {webm ? <source src={data.clip} type="video/mp4" /> : null}
          </video>
          {data.clip_caption ? (
            <figcaption className="mono border-t border-border px-4 py-3 text-[10px] tracking-widest text-muted-foreground">
              {data.clip_caption}
            </figcaption>
          ) : null}
        </figure>
      ) : null}

      {photos.length ? (
        <div
          data-testid="credibility-photos"
          className="mt-4 columns-1 gap-4 sm:columns-2 [&>figure]:mb-4"
        >
          {photos.map((p, i) => (
            <figure
              key={p.id || i}
              data-testid={`credibility-photo-${i}`}
              className="group relative break-inside-avoid overflow-hidden border border-border bg-[#141414]"
            >
              <img
                src={p.url}
                alt={p.caption || "Smash Lemonade at a live event"}
                loading="lazy"
                className="h-auto w-full transition-transform duration-500 group-hover:scale-105"
              />
              {p.caption ? (
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent px-4 pb-3 pt-10">
                  <span className="mono text-[10px] leading-relaxed tracking-widest text-white">
                    {p.caption}
                  </span>
                </figcaption>
              ) : null}
            </figure>
          ))}
        </div>
      ) : null}

      {milestones.length ? (
        <div data-testid="credibility-milestones" className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {milestones.map((m, i) => (
            <div
              key={m.id || i}
              data-testid={`credibility-milestone-${i}`}
              className="border-l-2 border-primary bg-[#141414] px-5 py-4"
            >
              <div className="display text-2xl text-primary">{m.label}</div>
              {m.detail ? <p className="mt-1 text-sm text-muted-foreground">{m.detail}</p> : null}
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
};

/** Everything-you-get value stack sitting directly above the purchase CTA. */
export const ValueStack = ({ product }) => {
  if (!product) return null;
  const closed = (product.effective_status || product.status) === "ENROLLMENT_CLOSED";
  return (
    <section
      id="academy"
      data-testid="value-stack"
      className="mx-auto max-w-4xl px-5 py-20 md:px-10 md:py-28"
    >
      <div className="border border-primary bg-[#141414] p-7 md:p-12">
        <Eyebrow>EVERYTHING YOU GET</Eyebrow>
        <h2 className="neon mt-3 text-4xl leading-tight sm:text-5xl">{product.headline}</h2>
        <p className="mt-4 text-base text-muted-foreground md:text-lg">{product.description}</p>

        <ul className="mt-9 grid gap-3 sm:grid-cols-2">
          {(product.features || []).map((f) => (
            <li
              key={f}
              className="flex items-start gap-3 border-b border-border/60 pb-3 text-base text-white"
            >
              <Check size={16} className="mt-1 shrink-0 text-primary" />
              {f}
            </li>
          ))}
        </ul>

        <div className="mt-10 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="mono text-[11px] tracking-[0.3em] text-primary">
              FOUNDING MEMBER ACCESS
            </div>
            <div className="display mt-1 text-6xl leading-none text-primary md:text-7xl">
              {product.sale_price ? `$${product.sale_price}` : product.price_label}
            </div>
          </div>
          <Link
            to={closed ? "/waitlist/popup_profits" : "/buy/popup_profits"}
            data-testid="value-stack-cta"
            className="display inline-flex items-center justify-center rounded-sm bg-primary px-8 py-5 text-xl uppercase text-primary-foreground transition-colors hover:bg-[#E6C800] md:text-2xl"
          >
            {closed ? "JOIN THE WAITLIST" : product.cta_text}
          </Link>
        </div>

        {product.note ? (
          <p
            data-testid="founding-price-note"
            className="mono mt-8 border-l-2 border-primary/60 pl-4 text-[11px] leading-relaxed text-muted-foreground"
          >
            {product.note}
          </p>
        ) : null}
      </div>
    </section>
  );
};
